import React, { useState } from 'react';
import { Button, Card, Col, DatePicker, Form, Input, Row, Space, Table, Typography, Tooltip, Modal } from 'antd';
import CampaignRequestorAdGroupsDialog from './CampaignRequestorAdGroupsDialog';
import moment from 'moment';
import S3Image from '../CampaignLauncher/components/S3Image';
import { DeleteOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

export const CampaignRequestorAdGroup = ({
  form,
  index,
  campaignIndex,
  lineItem = {}, // Reactive line item data from parent
  campaignType, // Campaign type from parent
  fieldKey,
  restField,
  name,
  editable = false,
  setPreviousValues,
}) => {
  // Use reactive props instead of reading from form instance
  const isVideoType = campaignType === 8; // Video campaign type
  const adGroup = lineItem.adGroup || {};
  const adGroupCreatives = (adGroup && adGroup.adGroupCreatives) || [];
  const allMedias = adGroupCreatives.reduce((acc, item) => {
    return acc.concat(item.medias);
  }, []);

  const [showAdGroupsModal, setShowAdGroupsModal] = useState(false);
  const [galleryModalVisible, setGalleryModalVisible] = useState(false);
  const [modalMedias, setModalMedias] = useState([]);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);

  const handleRemoveAdGroup = (record) => {
    const newAdGroupCreatives = adGroupCreatives.filter(
      (adGroupCreative) => adGroupCreative.name !== record.name
    );

    const currentFormData = form?.getFieldsValue() || {};
    const currentCampaigns = currentFormData.campaigns || [];
    const updatedCampaigns = [...currentCampaigns];

    // Use nested path: campaigns[campaignIndex].lineItems[index]
    if (updatedCampaigns[campaignIndex]?.lineItems?.[index]) {
      const currentLineItem = updatedCampaigns[campaignIndex].lineItems[index];
      updatedCampaigns[campaignIndex] = {
        ...updatedCampaigns[campaignIndex],
        lineItems: [
          ...updatedCampaigns[campaignIndex].lineItems.slice(0, index),
          {
            ...currentLineItem,
            adGroup: {
              ...(currentLineItem.adGroup || {}),
              adGroupCreatives: newAdGroupCreatives,
            },
          },
          ...updatedCampaigns[campaignIndex].lineItems.slice(index + 1),
        ],
      };

      // Use setPreviousValues which calls the parent's helper override
      // The parent expects campaigns to be the lineItems array for this campaign
      setPreviousValues && setPreviousValues({
        campaigns: updatedCampaigns[campaignIndex].lineItems,
      });
    }
  };

  return (
    <>
      <Card title="AD GROUP">
        <div className="requester-table-wrapper" style={{ marginTop: 20 }}>
          <div className="requester-table-header">
            <Row>
              <Col span={12}>
                <Title level={5}>DISPLAY AD GROUPS</Title>
              </Col>
              {editable && adGroupCreatives.length > 0 ? (
                <Col span={12}>
                  <div style={{ textAlign: 'end' }}>
                    <Button onClick={() => setShowAdGroupsModal(true)}>Edit Ad Groups</Button>
                  </div>
                </Col>
              ) : (
                <></>
              )}
            </Row>
          </div>
          {!adGroupCreatives.length ? (
            <div style={{ padding: 20 }}>
              <Space direction="vertical" size="small" align="center" style={{ width: '100%' }}>
                {editable ? (
                  <>
                    <Text>Select ad groups from your library to include their ad units</Text>
                    <Button
                      type="primary"
                      onClick={() => {
                        setShowAdGroupsModal(true);
                      }}
                    >
                      Select Ad Groups
                    </Button>
                  </>
                ) : (
                  <Text>No Ad Groups Selected</Text>
                )}
              </Space>
            </div>
          ) : (
            <>
              <Form.List
                name={[name, 'adGroup', 'adGroupCreatives']}
                fieldKey={[fieldKey, 'adGroup', 'adGroupCreatives']}
              >
                {(adGroupCreativesFields) => {
                  return (
                    <Table
                      key="name"
                      scroll={{ x: 800 }}
                      columns={[
                        {
                          title: 'Display Ad Group Name',
                          dataIndex: 'name',
                          key: 'name',
                          ellipsis: true,
                          width: 180,
                        },
                        {
                          title: isVideoType
                            ? 'Click-Through Link (Optional)'
                            : 'Click-Through Link',
                          dataIndex: 'clickthrough',
                          key: 'clickthrough',
                          ellipsis: true,
                          width: 150,
                          render: (clickthrough, record, index) => {
                            // In review mode, just display the text
                            if (!editable) {
                              return <Text>{record.clickthrough || '-'}</Text>;
                            }
                            // In edit mode, show the form input
                            return (
                              <Form.Item
                                {...adGroupCreativesFields[index]}
                                name={[index, 'clickthrough']}
                                fieldKey={[index, 'clickthrough']}
                                rules={[
                                  ...(isVideoType
                                    ? []
                                    : [
                                        {
                                          required: true,
                                          message: 'Click-through URL is required',
                                        },
                                      ]),
                                  {
                                    type: 'url',
                                    message: 'Please enter a valid URL',
                                  },
                                ]}
                              >
                                <Input disabled={!editable} />
                              </Form.Item>
                            );
                          },
                        },
                        {
                          title: 'Duration',
                          dataIndex: 'duration',
                          key: 'duration',
                          width: 260,
                          render: (_, record, index) => {
                            // In review mode, just display the dates as text
                            if (!editable) {
                              return (
                                <Space direction="vertical" size="small">
                                  <Text>Start: {record.startDate ? moment(record.startDate).format('MM/DD/YYYY') : '-'}</Text>
                                  <Text>End: {record.endDate ? moment(record.endDate).format('MM/DD/YYYY') : '-'}</Text>
                                </Space>
                              );
                            }
                            // In edit mode, show the form inputs
                            return (
                              <Row gutter={8}>
                                <Col xs={24} sm={12}>
                                  <Space
                                    direction="vertical"
                                    size="small"
                                    style={{ width: '100%', minWidth: '120px' }}
                                  >
                                    <Text>Start Date</Text>
                                    <Form.Item
                                      {...adGroupCreativesFields[index]}
                                      name={[index, 'startDate']}
                                      fieldKey={[index, 'startDate']}
                                    >
                                      <DatePicker disabled={!editable} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Space>
                                </Col>
                                <Col xs={24} sm={12}>
                                  <Space
                                    direction="vertical"
                                    size="small"
                                    style={{ width: '100%', minWidth: '120px' }}
                                  >
                                    <Text>End Date</Text>
                                    <Form.Item
                                      {...adGroupCreativesFields[index]}
                                      name={[index, 'endDate']}
                                      fieldKey={[index, 'endDate']}
                                    >
                                      <DatePicker disabled={!editable} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Space>
                                </Col>
                              </Row>
                            );
                          },
                        },
                        {
                          title: 'Gallery',
                          dataIndex: 'medias',
                          key: 'medias',
                          width: 180,
                          render: (medias) => {
                            if (!medias || medias.length === 0) {
                              return <Text type="secondary">-</Text>;
                            }

                            const handleOpenGallery = () => {
                              setModalMedias(medias);
                              setSelectedMediaIndex(0);
                              setGalleryModalVisible(true);
                            };

                            return (
                              <Space size="small">
                                {medias.slice(0, 1).map((media, index) => (
                                  <div
                                    key={index}
                                    style={{ cursor: 'pointer' }}
                                    onClick={handleOpenGallery}
                                  >
                                    <S3Image imgKey={media.key} preview={false} />
                                  </div>
                                ))}
                                {medias.length > 1 && (
                                  <Tooltip title={`View all ${medias.length} creatives`}>
                                    <div
                                      style={{
                                        width: 50,
                                        height: 40,
                                        border: '1px dashed #d9d9d9',
                                        borderRadius: 4,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#fafafa',
                                        cursor: 'pointer',
                                      }}
                                      onClick={handleOpenGallery}
                                    >
                                      <Text type="secondary" style={{ fontSize: 12 }}>
                                        +{medias.length - 1}
                                      </Text>
                                    </div>
                                  </Tooltip>
                                )}
                              </Space>
                            );
                          },
                        },
                        {
                          title: '',
                          dataIndex: 'actions',
                          key: 'actions',
                          width: 60,
                          render: (_, record) => {
                            if (!editable) return null;
                            return (
                              <Space size="small" align="end" style={{ width: '100%' }}>
                                <Button
                                  type="text"
                                  icon={<DeleteOutlined />}
                                  danger
                                  onClick={() => handleRemoveAdGroup(record)}
                                ></Button>
                              </Space>
                            );
                          },
                        },
                      ]}
                      dataSource={adGroupCreatives}
                      pagination={false}
                      className="requester-table"
                      summary={() => (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={2}>
                            {adGroupCreatives.length} Display Ad Group(s)
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} colSpan={2}>
                            {allMedias.length} Display Ad Unit(s)
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                    />
                  );
                }}
              </Form.List>
            </>
          )}
        </div>

        {/* Create hidden input for adGroupCreatives*/}
        <Form.Item
          {...restField}
          name={[name, 'adGroup', 'adGroupCreatives']}
          fieldKey={[fieldKey, 'adGroup', 'adGroupCreatives']}
          hidden
          rules={[
            {
              required: true,
              message: `Please select ad groups for ${lineItem?.name || 'this line item'}`,
            },
          ]}
        >
          <Input />
        </Form.Item>
      </Card>
      {showAdGroupsModal && (
        <CampaignRequestorAdGroupsDialog
          visible={showAdGroupsModal}
          setVisible={setShowAdGroupsModal}
          orgId={form?.getFieldValue('advertiserId')}
          selectedAdGroupCreatives={adGroupCreatives}
          onAddSelections={(creatives) => {
            
            const currentFormData = form?.getFieldsValue() || {};

            const currentCampaigns = currentFormData.campaigns || [];
            const updatedCampaigns = [...currentCampaigns];

            // Use nested path: campaigns[campaignIndex].lineItems[index]

            if (updatedCampaigns[campaignIndex]?.lineItems?.[index]) {
              const currentLineItem = updatedCampaigns[campaignIndex].lineItems[index];
              updatedCampaigns[campaignIndex] = {
                ...updatedCampaigns[campaignIndex],
                lineItems: [
                  ...updatedCampaigns[campaignIndex].lineItems.slice(0, index),
                  {
                    ...currentLineItem,
                    adGroup: {
                      ...(currentLineItem.adGroup || {}),
                      adGroupCreatives: creatives.map((creative) => {
                        // Check if creative already exists in the list
                        const existingCreative = adGroupCreatives.find(
                          (a) => a.name === creative.name
                        );

                        if (existingCreative) {
                          return existingCreative;
                        }

                        delete creative.id;
                        return {
                          ...creative,
                          startDate:
                            currentLineItem.adGroup && currentLineItem.adGroup.startDate
                              ? moment(currentLineItem.adGroup.startDate)
                              : moment(currentLineItem.startDate || form?.getFieldValue('startDate')),
                          endDate:
                            currentLineItem.adGroup && currentLineItem.adGroup.endDate
                              ? moment(currentLineItem.adGroup.endDate)
                              : moment(currentLineItem.endDate || form?.getFieldValue('endDate')),
                        };
                      }),
                    },
                  },
                  ...updatedCampaigns[campaignIndex].lineItems.slice(index + 1),
                ],
              };


              // Use setPreviousValues which calls the parent's helper override
              // The parent expects campaigns to be the lineItems array for this campaign
              setPreviousValues &&
                setPreviousValues({
                  campaigns: updatedCampaigns[campaignIndex].lineItems,
                });


              // Trigger validation for clickthrough fields with nested path
              setTimeout(() => {
                creatives.forEach((_, idx) => {
                  form
                    ?.validateFields([
                      [
                        'campaigns',
                        campaignIndex,
                        'lineItems',
                        index,
                        'adGroup',
                        'adGroupCreatives',
                        idx,
                        'clickthrough',
                      ],
                    ])
                    .catch(() => {});
                });
              }, 0);
            }
          }}
        />
      )}

      {/* Gallery Modal */}
      <Modal
        open={galleryModalVisible}
        onCancel={() => setGalleryModalVisible(false)}
        footer={null}
        width={1000}
        centered
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Gallery Preview</span>
            <Text type="secondary">
              {selectedMediaIndex + 1} / {modalMedias.length}
            </Text>
          </div>
        }
      >
        {modalMedias.length > 0 && (
          <div style={{ position: 'relative' }}>
            <div style={{ textAlign: 'center', padding: '20px 0', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <S3Image
                imgKey={modalMedias[selectedMediaIndex]?.key}
                width={900}
                height={600}
                preview={false}
              />
            </div>

            {modalMedias.length > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                <Button
                  icon={<LeftOutlined />}
                  onClick={() => {
                    setSelectedMediaIndex((prev) => (prev > 0 ? prev - 1 : modalMedias.length - 1));
                  }}
                >
                  Previous
                </Button>
                <Button
                  onClick={() => {
                    setSelectedMediaIndex((prev) => (prev < modalMedias.length - 1 ? prev + 1 : 0));
                  }}
                >
                  Next <RightOutlined />
                </Button>
              </div>
            )}

            {modalMedias[selectedMediaIndex]?.name && (
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Text type="secondary">{modalMedias[selectedMediaIndex].name}</Text>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};
